import React, { useEffect, useState } from "react";
import { IoCloseOutline, IoTimeOutline, IoRefreshOutline } from "react-icons/io5";
import styles from "./VersionHistoryModal.module.css";

function formatRelativeVersionDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMin = Math.floor((now - date) / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round((startOfToday - startOfDate) / 86400000);
  const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  if (dayDiff === 1) return `Yesterday, ${timeStr}`;
  if (dayDiff < 7) return `${date.toLocaleDateString(undefined, { weekday: "short" })}, ${timeStr}`;
  if (dayDiff < 14) return "Last week";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function VersionHistoryModal({
  loading,
  error,
  versions,
  canRestore,
  restoringId,
  onRestore,
  onClose,
}) {
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const timelineRows = [
    { id: "current", label: "Autosave", created_at: new Date().toISOString(), created_by: "You", current: true },
    ...versions,
  ];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h2>Version history</h2>
            <p className={styles.subtitle}>Restore or preview any earlier state.</p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <IoCloseOutline />
          </button>
        </div>

        {loading && (
          <div className={styles.stateBlock}>
            <IoRefreshOutline className={styles.spinIcon} />
            <p>Loading versions…</p>
          </div>
        )}

        {!loading && error && (
          <div className={styles.stateBlock}>
            <IoTimeOutline />
            <p>Couldn't load version history.</p>
            <p className={styles.stateSubtext}>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <ul className={styles.timeline}>
            {timelineRows.map((version) => (
              <li key={version.id} className={styles.row}>
                <span className={`${styles.dot} ${version.current ? styles.dotCurrent : ""}`} />
                <div className={styles.rowBody}>
                  <div className={styles.rowTitleLine}>
                    <span
                      className={styles.rowTitle}
                      title={version.label || `Version ${version.id}`}
                    >
                      {version.label || `Version ${version.id}`}
                    </span>
                    {version.current && <span className={styles.currentBadge}>CURRENT</span>}
                  </div>
                  <span className={styles.rowMeta}>
                    {formatRelativeVersionDate(version.created_at)}
                  </span>

                  {!version.current && (
                    <div className={styles.rowActions}>
                      {canRestore && (
                        <button
                          type="button"
                          className={styles.restoreBtn}
                          onClick={() => onRestore(version)}
                          disabled={restoringId === version.id}
                        >
                          {restoringId === version.id ? "Restoring…" : "Restore"}
                        </button>
                      )}
                      <button
                        type="button"
                        className={styles.previewBtn}
                        onClick={() => setExpandedId((prev) => (prev === version.id ? null : version.id))}
                      >
                        {expandedId === version.id ? "Hide preview" : "Preview"}
                      </button>
                    </div>
                  )}

                  {expandedId === version.id && (
                    <div className={styles.previewPanel}>
                      <p className={styles.previewHeading}>
                        {version.categories?.length
                          ? `${version.categories.length} categor${version.categories.length === 1 ? "y" : "ies"} in this version`
                          : "Categories in this version"}
                      </p>
                      {version.categories?.length ? (
                        version.categories.map((cat, idx) => (
                          <div key={`${version.id}-${idx}`} className={styles.previewRow}>
                            <span className={styles.previewName} title={cat.name}>
                              {cat.name}
                            </span>
                            <span className={styles.previewMeta}>{cat.layout_type}</span>
                          </div>
                        ))
                      ) : (
                        <p className={styles.previewEmpty}>No categories in this version.</p>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
